import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useSession } from '@/features/auth/use-session'
import { updateThemePreference } from './api'
import { applyBrowserUiColor } from './browser-ui'
import { ThemeContext } from './theme-context'
import { DEFAULT_THEME, isThemeId, type ThemeId } from './types'

const STORAGE_KEY = 'mlog.theme'

function readStoredTheme(): ThemeId {
  const stored = localStorage.getItem(STORAGE_KEY)
  return isThemeId(stored) ? stored : DEFAULT_THEME
}

/**
 * 테마 전환은 문서 루트의 data-theme 값을 바꾸는 것으로 끝난다.
 * 유틸리티가 참조하는 --color-* 변수만 테마별로 갈린다.
 * 값을 고르는 자리가 셋이라 앞뒤를 정한다. 이번 방문에서 고른 값이 가장 앞서고, 없으면 계정에
 * 남은 값을, 그것도 없으면 이 브라우저에 남은 값을 쓴다.
 *
 * 계정 값이 이번 방문의 선택을 덮지 않는다. 화면을 옮길 때마다 서버 값이 다시 오는데 덮게 두면
 * 방금 고른 테마가 되돌아간다. 다른 기기에서 바꾼 값은 이번 방문에 고른 적이 없을 때 따라오고
 * 고른 적이 있으면 다음 방문에 따라온다.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const session = useSession()
  const [storedTheme] = useState(readStoredTheme)
  const [selectedTheme, setSelectedTheme] = useState<ThemeId | null>(null)
  const { mutate: saveTheme } = useMutation({ mutationFn: updateThemePreference })

  const themeId = selectedTheme ?? session.preferences?.themeId ?? storedTheme

  useEffect(() => {
    document.documentElement.dataset.theme = themeId
    localStorage.setItem(STORAGE_KEY, themeId)
    applyBrowserUiColor(themeId)
  }, [themeId])

  const setTheme = useCallback(
    (next: ThemeId) => {
      setSelectedTheme(next)
      if (session.status === 'authenticated') {
        saveTheme({ themeId: next })
      }
    },
    [saveTheme, session.status],
  )

  const value = useMemo(() => ({ themeId, setTheme }), [themeId, setTheme])

  return <ThemeContext value={value}>{children}</ThemeContext>
}
